import React, { useEffect, useMemo, useState } from 'react'
import { Alert } from '@mui/material'
import { authFetch, fetchWithAuth, BASE_BACKEND_URL } from '../auth'
import { formatMoney } from '../helpers/utils'
import { getTimestampFromDateTime } from '../helpers/formatDate'
import UbiquaDataTable from '../components/table/UbiquaDataTable'
import UbiquaTablePagination from '../components/table/UbiquaTablePagination'
import AppChip from '../components/chips/AppChip'
import FilterUbiquaMui from '../components/Filters/FilterUbiquaMui/FilterUbiquaMui'
import { buildSelectValue, getSelectValues } from '../components/Filters/filterSelectState'
import DateRangeFilter from '../components/DateRangeFilter'
import useSettlementsListFilters from './helpers/useSettlementsListFilters'
import LoadingScreen from '../components/LoadingScreen'
import { canDownloadSettlements } from '../global_context/capabilities/settlementCapabilities'
import {
  SETTLEMENT_INDEX_TEXT,
  SETTLEMENT_LIST_COLUMNS,
  SETTLEMENT_LIST_CONFIG,
  SETTLEMENT_LIST_FILTERS,
  SETTLEMENT_LIST_TEXT
} from './constants/constants'
import { exportSettlementsTableCsv } from './helpers/exports/exportSettlementsTableCsv'
import {
  createEmptyDateRange,
  formatDateRangeSummary,
  pickerValueToDateRange
} from '../report/unified/helpers/routeExecutionFilters'
import {
  SETTLEMENT_FILTER_KEYS,
  buildSettlementLabelMapping,
  buildSettlementsFilterQuery,
  buildSettlementsIndexQueryString,
  getSettlementStatusUI
} from './helpers/indexFilters'

export default function SettlementsListTab() {
  const [isFetching, setIsFetching] = useState(true)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [filtersLoading, setFiltersLoading] = useState(true)
  const [error, setError] = useState('')
  const {
    query,
    searchInput,
    setSearchInput,
    currentPage,
    perPage,
    allSelectedOptions,
    dateRange,
    update
  } = useSettlementsListFilters()
  const [totalCount, setTotalCount] = useState(0)
  const [data, setData] = useState([])
  const [settlements, setSettlements] = useState([])

  const totalPages = Math.ceil(totalCount / perPage)
  const filterQuery = useMemo(
    () => buildSettlementsFilterQuery(allSelectedOptions),
    [allSelectedOptions]
  )
  const dateQuery = dateRange.start
    ? `&start_date=${dateRange.start.startOf('day').toISOString()}&end_date=${dateRange.end.endOf('day').toISOString()}`
    : null

  useEffect(() => {
    const controller = new AbortController()
    setFiltersLoading(true)
    fetchWithAuth(
      `${BASE_BACKEND_URL}/settlements?client_names_only=true`,
      { method: 'GET', signal: controller.signal }
    )
      .then((response) => {
        if (!controller.signal.aborted) setSettlements(response?.result || [])
      })
      .catch(() => {
        if (!controller.signal.aborted) setSettlements([])
      })
      .finally(() => {
        if (!controller.signal.aborted) setFiltersLoading(false)
      })
    return () => controller.abort()
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    const fetchSettlements = async () => {
      setIsFetching(true)
      setError('')
      try {
        const queryString = buildSettlementsIndexQueryString({
          currentPage,
          perPage,
          filterQuery,
          dateQuery,
          query
        })
        const response = await authFetch(`${BASE_BACKEND_URL}/settlements?${queryString}`, {
          method: 'GET',
          signal: controller.signal,
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
        })
        if (!response || response.status !== 200)
          throw new Error(SETTLEMENT_LIST_TEXT.LOAD_RETRY_ERROR)
        const result = await response.json()
        if (controller.signal.aborted) return
        const rows = Array.isArray(result) ? result : result.result || []
        setData(rows)
        setHasLoaded(true)
        setTotalCount(result.count ?? rows.length)
      } catch (reason) {
        if (controller.signal.aborted) return
        setData([])
        setTotalCount(0)
        setError(reason.message || SETTLEMENT_LIST_TEXT.LOAD_ERROR)
      } finally {
        if (!controller.signal.aborted) setIsFetching(false)
      }
    }
    fetchSettlements()
    return () => controller.abort()
  }, [query, filterQuery, dateQuery, currentPage, perPage])

  const { filters, labelToIdMapping } = useMemo(
    () => buildSettlementLabelMapping(settlements, true),
    [settlements]
  )
  const ubiquaFilters = filters.map((filter) => ({
    key: filter.key,
    ...SETTLEMENT_LIST_FILTERS[filter.key],
    loading: filter.key === SETTLEMENT_FILTER_KEYS.CREATED_BY && filtersLoading,
    options: filter.value.map((label) => ({ label, value: labelToIdMapping[label] }))
  }))
  const appliedFilterSelections = Object.fromEntries(
    filters.map(({ key }) => [key, buildSelectValue(allSelectedOptions.get(key))])
  )
  const changeFilter = (key, selection) => {
    const parameter = key === SETTLEMENT_FILTER_KEYS.CREATED_BY ? 'client_uid' : 'status'
    update({ [parameter]: getSelectValues(selection).join(',') })
  }
  const applyDateRange = (range) => {
    update({
      start_date: range.start && range.end ? range.start.startOf('day').toISOString() : null,
      end_date: range.start && range.end ? range.end.endOf('day').toISOString() : null
    })
  }
  const resetFilters = () => {
    setSearchInput('')
    update({ q: null, client_uid: null, status: null, start_date: null, end_date: null })
  }
  const clearSearch = () => {
    setSearchInput('')
    update({ q: null })
  }
  const columnRenderers = {
    collected_at: (row) => getTimestampFromDateTime(row.collected_at),
    uid: (row) => <span className="ubiqua-table-title ubiqua-code">{row.uid}</span>,
    user_uid: (row) => (
      <span className="ubiqua-identity-stacked">
        <span className="ubiqua-identity-stacked__name" title={row.user_full_name}>
          {row.user_full_name || SETTLEMENT_LIST_TEXT.MISSING_VALUE}
        </span>
        <span className="ubiqua-identity-stacked__code" title={row.user_uid}>
          {row.user_uid || SETTLEMENT_LIST_TEXT.MISSING_VALUE}
        </span>
      </span>
    ),
    erp_document_reference: (row) =>
      row.erp_document_reference || SETTLEMENT_LIST_TEXT.MISSING_VALUE,
    total_amount: (row) => <span className="ubiqua-money">{formatMoney(row.total_amount)}</span>,
    status: (row) => {
      const status = getSettlementStatusUI(row.approval_status || row.status)
      return status.label ? (
        <AppChip label={status.label} variant={status.variant} />
      ) : (
        SETTLEMENT_LIST_TEXT.MISSING_VALUE
      )
    }
  }
  const columns = SETTLEMENT_LIST_COLUMNS.map((column) => ({
    ...column,
    render: columnRenderers[column.key]
  }))

  if (isFetching && !hasLoaded) {
    return <LoadingScreen text={SETTLEMENT_LIST_TEXT.LOADING} minHeight="320px" />
  }

  return (
    <section aria-label={SETTLEMENT_LIST_TEXT.ARIA_LABEL}>
      <div className="app-content-enter">
        {error && <Alert severity="error">{error}</Alert>}
        <FilterUbiquaMui
          title={SETTLEMENT_LIST_TEXT.FILTER_TITLE}
          searchPlaceholder={SETTLEMENT_LIST_TEXT.SEARCH_PLACEHOLDER}
          compactSearch
          filters={ubiquaFilters}
          appliedFilterSelections={appliedFilterSelections}
          isAnyFilterSelected={Boolean(filterQuery || dateQuery || query)}
          searchInput={searchInput}
          appliedSearchInput={query}
          defaultInlineFilterCount={SETTLEMENT_LIST_CONFIG.INLINE_FILTER_COUNT}
          supplementaryFilterCount={SETTLEMENT_LIST_CONFIG.SUPPLEMENTARY_FILTER_COUNT}
          showExport={canDownloadSettlements()}
          onExport={() => exportSettlementsTableCsv(data)}
          exportDisabled={isFetching || data.length === 0}
          exportTooltip={SETTLEMENT_LIST_TEXT.EXPORT_TOOLTIP}
          onFilterChange={changeFilter}
          onResetFilters={resetFilters}
          onSearchChange={(event) => {
            setSearchInput(event.target.value)
          }}
          onSearchEnter={(event) => {
            if (event.key !== 'Enter') return
            event.preventDefault()
            update({ q: searchInput })
          }}
          onSearchClear={clearSearch}
          controlsBeforeFirstFilterContent={
            <DateRangeFilter
              variant="filter"
              inputTitle={SETTLEMENT_LIST_TEXT.DATE_TITLE}
              placeholder={SETTLEMENT_LIST_TEXT.DATE_PLACEHOLDER}
              value={dateRange.start ? [dateRange.start, dateRange.end] : null}
              onChange={(dates) => applyDateRange(pickerValueToDateRange(dates))}
            />
          }
          supplementaryActiveCount={dateRange.start ? 1 : 0}
          supplementaryAppliedGroups={
            dateRange.start
              ? [
                  {
                    key: SETTLEMENT_LIST_CONFIG.DATE_RANGE_KEY,
                    label: SETTLEMENT_LIST_TEXT.DATE_LABEL,
                    values: [
                      {
                        value: SETTLEMENT_LIST_CONFIG.DATE_RANGE_KEY,
                        label: formatDateRangeSummary(dateRange)
                      }
                    ],
                    onRemove: () => applyDateRange(createEmptyDateRange())
                  }
                ]
              : []
          }
        />
        <UbiquaDataTable
          ariaLabel={SETTLEMENT_INDEX_TEXT.TITLE}
          columns={columns}
          rows={data}
          rowKey={SETTLEMENT_LIST_CONFIG.ROW_KEY}
          loading={isFetching}
          emptyContent={SETTLEMENT_LIST_TEXT.EMPTY}
          onRowClick={(row) => window.open(`/liquidaciones/detalles/${row.uuid}`, '_blank')}
        />
        {totalCount > SETTLEMENT_LIST_CONFIG.PAGINATION_THRESHOLD && (
          <UbiquaTablePagination
            ariaLabel={SETTLEMENT_LIST_TEXT.PAGINATION_ARIA_LABEL}
            itemLabel={SETTLEMENT_LIST_TEXT.PAGINATION_ITEM_LABEL}
            disabled={isFetching}
            pagination={{
              total_count: totalCount,
              per_page: perPage,
              total_pages: totalPages,
              current_page: currentPage
            }}
            onPageChange={(_, page) => update({ page })}
            onRowsPerPageChange={(_, size) => update({ per_page: size })}
          />
        )}
      </div>
    </section>
  )
}
